import { normalizeStockQuantity } from '@erp/domain/inventory';
import { isQuantityValidForUnit } from '@erp/domain/sales';

/**
 * Sous-ensemble d'une ligne de bon de réception nécessaire pour calculer son
 * effet de stock. Une ligne libre (sans `articleId`) reste sur le document mais
 * ne peut pas modifier le stock.
 */
export interface PurchaseReceptionStockLine {
  articleId?: string | null;
  quantity: number;
  /** Unité figée sur la ligne (`box`, `kg`, `pcs`, ...). */
  unit?: string | null;
}

/**
 * Effet canonique d'un bon de réception : une seule entrée par article.
 * Le dépôt, le document de référence et la date relèvent de l'enveloppe de
 * persistance ; ce helper ne calcule que la conséquence métier des lignes.
 */
export interface PurchaseReceptionStockEffect {
  articleId: string;
  movementType: 'in';
  quantity: number;
}

export class PurchaseReceptionStockValidationError extends Error {
  constructor(
    public readonly code:
      | 'sales.validation.quantityPositive'
      | 'sales.validation.quantityPrecision'
      | 'sales.validation.quantityWholeUnit',
    public readonly lineIndex: number,
  ) {
    super(code);
    this.name = 'PurchaseReceptionStockValidationError';
  }
}

/**
 * Construit les entrées de stock d'un bon de réception.
 *
 * - les lignes libres sont ignorées pour le STOCK, mais leur quantité est
 *   quand même validée car elle sera persistée et participe aux totaux ;
 * - chaque quantité doit déjà être représentable avec les 3 décimales de
 *   `NUMERIC(14,3)` : on ne l'arrondit jamais après calcul des totaux ;
 * - la règle d'unité partagée reste souveraine : les unités mesurables et la
 *   pièce acceptent les décimales, tandis que le carton exige un entier ;
 * - un article présent plusieurs fois produit un seul effet ;
 * - le résultat est trié par identifiant d'article, donc indépendant de
 *   l'ordre des lignes et directement comparable lors d'une réconciliation.
 */
export function buildPurchaseReceptionStockEffects(
  lines: readonly PurchaseReceptionStockLine[],
): PurchaseReceptionStockEffect[] {
  const quantityByArticle = new Map<string, number>();

  lines.forEach((line, lineIndex) => {
    const rawQuantity = Number(line.quantity);
    if (!Number.isFinite(rawQuantity) || rawQuantity <= 0) {
      throw new PurchaseReceptionStockValidationError(
        'sales.validation.quantityPositive',
        lineIndex,
      );
    }

    const quantity = normalizeStockQuantity(rawQuantity);
    if (Math.abs(rawQuantity - quantity) > 1e-9) {
      throw new PurchaseReceptionStockValidationError(
        'sales.validation.quantityPrecision',
        lineIndex,
      );
    }

    if (!line.articleId) return;
    if (!isQuantityValidForUnit(quantity, line.unit)) {
      throw new PurchaseReceptionStockValidationError(
        'sales.validation.quantityWholeUnit',
        lineIndex,
      );
    }

    quantityByArticle.set(
      line.articleId,
      normalizeStockQuantity(
        (quantityByArticle.get(line.articleId) ?? 0) + quantity,
      ),
    );
  });

  return [...quantityByArticle.entries()]
    .sort(([leftArticleId], [rightArticleId]) =>
      leftArticleId < rightArticleId
        ? -1
        : leftArticleId > rightArticleId
          ? 1
          : 0,
    )
    .map(([articleId, quantity]) => ({
      articleId,
      movementType: 'in' as const,
      quantity,
    }));
}
